import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { v, num, attachmentLabel, hasAttachmentRef, Checkbox, ClauseBlock, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
import { parseDateValue, parseNumberValue, postSigningRule, subtractMonths, } from '../../../notification-rules.js';
const garageNotificationRules = [
    postSigningRule({
        ruleKey: 'termination_notice',
        kind: 'termination_notice',
        label: 'Uppsägning',
        resolveTriggerAt: (values) => {
            const endDate = parseDateValue(values.end_date);
            const notice = parseNumberValue(values.notice_months);
            if (!endDate || notice === null || notice <= 0)
                return null;
            return subtractMonths(endDate, notice);
        },
    }),
    postSigningRule({
        ruleKey: 'lease_end',
        kind: 'lease_end',
        label: 'Garagekontraktet löper ut',
        occurrenceLabel: 'Garagekontraktet löper ut',
        resolveTriggerAt: (values) => parseDateValue(values.end_date),
    }),
];
export const metadata = {
    // Document sections in fixed order (per ADR-0049). § numbers are
    // computed from visibility; refs are the stable identity. Field-less
    // sections (static text / signing / bilagor) are declared so they
    // get numbered even though they carry no editor field.
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'garage', title: 'Garage / Garageplats' },
        { ref: 'anvandning', title: 'Användning' },
        { ref: 'samnyttjande', title: 'Samnyttjande' },
        { ref: 'elstrom', title: 'Elström' },
        { ref: 'hyrestid', title: 'Hyrestid' },
        { ref: 'uppsagning', title: 'Uppsägning och förlängning' },
        { ref: 'hyra', title: 'Hyra' },
        { ref: 'andring-hyra', title: 'Ändring av hyra' },
        { ref: 'elkostnad', title: 'Kostnad för elström' },
        { ref: 'moms', title: 'Mervärdesskatt (moms)' },
        { ref: 'betalning', title: 'Hyrans betalning' },
        { ref: 'personuppgifter', title: 'Hantering av personuppgifter' },
        { ref: 'sarskilda-bestammelser', title: 'Särskilda bestämmelser' },
        { ref: 'underskrift', title: 'Underskrifter' },
    ],
    inputSections: [
        { id: 'anvandning-villkor', label: 'Användning & villkor', sectionRefs: ['anvandning', 'samnyttjande', 'elstrom'] },
        { id: 'hyrestid', label: 'Hyrestid & uppsägning', sectionRefs: ['hyrestid', 'uppsagning'] },
        { id: 'hyra-avgifter', label: 'Hyra & avgifter', sectionRefs: ['hyra', 'andring-hyra', 'elkostnad', 'moms', 'betalning'] },
        { id: 'ovrigt', label: 'Övrigt', sectionRefs: ['personuppgifter', 'sarskilda-bestammelser'] },
    ],
    templateKey: 'garage',
    version: 'v1',
    category: 'Garage',
    title: 'Hyreskontrakt för garage / garageplats',
    shortTitle: 'Garagekontrakt',
    titleSuffix: { key: 'garage_place_number', prefix: 'plats' },
    formNumber: 'Formulär R-13',
    preamble: 'Mellan hyresvärd och hyresgäst har följande avtal om upplåtelse av garage eller garageplats ingåtts.',
    description: 'Hyreskontrakt för helt garage eller garageplats (kall- eller varmgarage). Eget hyreskontrakt — inte kopplat till ett bostadsavtal.\n\n' +
        'Vem undertecknar: hyresvärden och hyresgästen via Scrive.\n\n' +
        'För enklare upplåtelse av mark som bilplats (öppet, utomhus) — använd istället mallen Bilplatsavtal.',
    status: 'active',
    signingMode: 'scrive',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
        },
        {
            id: 'lessee',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
        },
    ],
    // Party projection (ADR-0065) — tenant's address is the aviseringsadress.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'landlord_address', requiredAtFinal: true },
                { attr: 'phone', key: 'landlord_phone' },
            ],
        },
        {
            slot: 'lessee',
            section: 'hyresgast',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                {
                    attr: 'id_number',
                    key: 'tenant_personnummer',
                    requiredAtFinal: true,
                    help: 'PII — ej sökbart',
                },
                {
                    attr: 'address',
                    key: 'tenant_billing_address',
                    label: 'Aviseringsadress',
                    requiredAtFinal: true,
                },
                { attr: 'phone', key: 'tenant_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Fastighet',
            multi: false,
            required: true,
        },
    ],
    notificationRules: garageNotificationRules,
};
export const variableSchema = [
    // Hyresvärd / Hyresgäst — generated from metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // Garage / Garageplats
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'garage',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'garage',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'garage_street',
        label: 'Adress för fastigheten',
        type: 'text',
        section: 'garage',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'garage_form',
        label: 'Typ',
        type: 'select',
        section: 'garage',
        requiredAtFinal: true,
        default: 'Garageplats',
        options: [
            { value: 'Helt garage', label: 'Helt garage', code: 'whole_garage', summary: 'The lease covers an entire garage.' },
            { value: 'Garageplats', label: 'Garageplats (i större anläggning)', code: 'garage_space', summary: 'The lease covers a garage space within a larger facility.' },
        ],
    },
    {
        key: 'garage_place_number',
        label: 'Garageplats nr',
        type: 'text',
        section: 'garage',
        visibleWhen: { garage_form: 'Garageplats' },
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'garage_temperature',
        label: 'Temperatur',
        type: 'select',
        section: 'garage',
        requiredAtFinal: true,
        default: 'Kallgarage',
        options: [
            { value: 'Kallgarage', label: 'Kallgarage', code: 'cold_garage', summary: 'Unheated (cold) garage.' },
            { value: 'Varmgarage', label: 'Varmgarage', code: 'heated_garage', summary: 'Heated (warm) garage.' },
        ],
    },
    {
        key: 'area_sqm',
        label: 'Area (ca m²)',
        type: 'number',
        min: 0,
        section: 'garage',
    },
    // Användning
    {
        key: 'car_count',
        label: 'Antal personbilar',
        type: 'number',
        min: 0,
        integer: true,
        section: 'anvandning',
        requiredAtFinal: true,
        default: 1,
    },
    {
        key: 'ev_count',
        label: 'Varav laddbara el-/hybridbilar',
        type: 'number',
        min: 0,
        integer: true,
        section: 'anvandning',
        default: 0,
    },
    // Samnyttjande
    {
        key: 'shared_use',
        label: 'Bilplatsen får nyttjas',
        type: 'select',
        section: 'samnyttjande',
        requiredAtFinal: true,
        default: 'Alla dagar och tider',
        options: [
            { value: 'Alla dagar och tider', label: 'Alla dagar och tider', code: 'all_days_times', summary: 'May be used on all days and at all times.' },
            { value: 'Nedanstående dagar och tider', label: 'Nedanstående dagar och tider', code: 'specified_days_times', summary: 'May be used only on the days and times specified below.' },
            { value: 'Enligt bilaga', label: 'Enligt bilaga', code: 'per_annex', summary: 'Usage times are specified in an annex.' },
        ],
    },
    {
        key: 'shared_use_annex',
        label: 'Bilaga – nyttjandetider',
        type: 'attachment_ref',
        section: 'samnyttjande',
        requiredAtFinal: true,
        attachmentDefaultName: 'Nyttjandetider',
        visibleWhen: { shared_use: 'Enligt bilaga' },
        help: 'Välj eller skapa en bilaga som anger nyttjandetiderna.',
    },
    {
        key: 'shared_days',
        label: 'Veckodagar',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Nedanstående dagar och tider' },
        help: 'T.ex. "måndag–fredag"',
    },
    {
        key: 'shared_time_from',
        label: 'Från kl',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Nedanstående dagar och tider' },
        help: 'T.ex. "08:00"',
    },
    {
        key: 'shared_time_to',
        label: 'Till kl',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Nedanstående dagar och tider' },
    },
    // Elström
    {
        key: 'electricity',
        label: 'Elanslutning',
        type: 'select',
        section: 'elstrom',
        requiredAtFinal: true,
        default: 'Utan elanslutning',
        options: [
            { value: 'Utan elanslutning', label: 'Utan elanslutning', code: 'no_connection', summary: 'No electrical connection.' },
            {
                value: 'Med elanslutning för motorvärmare/kupévärmare',
                label: 'Med elanslutning för motorvärmare/kupévärmare',
                code: 'engine_heater',
                summary: 'Electrical connection for an engine or cabin heater.',
            },
            {
                value: 'Med elanslutning för annan användning',
                label: 'Med elanslutning för annan användning',
                code: 'other_use',
                summary: 'Electrical connection for some other use, with a stated max wattage.',
            },
            { value: 'Med laddare', label: 'Med laddare', code: 'ev_charger', summary: 'Electrical connection with an EV charger.' },
        ],
    },
    {
        key: 'electricity_max_watt',
        label: 'Max effekt (watt)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'elstrom',
        visibleWhen: { electricity: 'Med elanslutning för annan användning' },
    },
    {
        key: 'charger_type',
        label: 'Anslutningstyp (laddare)',
        type: 'text',
        section: 'elstrom',
        visibleWhen: { electricity: 'Med laddare' },
        help: 'T.ex. Type 2',
    },
    // Hyrestid
    {
        key: 'start_date',
        label: 'Fr.o.m.',
        type: 'date',
        section: 'hyrestid',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'end_date',
        label: 'T.o.m.',
        type: 'date',
        section: 'hyrestid',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Uppsägning och förlängning
    {
        key: 'notice_months',
        label: 'Uppsägningstid (mån)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        requiredAtFinal: true,
        default: 3,
    },
    {
        key: 'extension_months',
        label: 'Förlängningstid (mån)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        requiredAtFinal: true,
        default: 12,
    },
    // Hyra
    {
        key: 'annual_rent',
        label: 'Hyra (kr/år)',
        type: 'currency',
        min: 0,
        section: 'hyra',
        requiredAtFinal: true,
        // Monetary amount — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'rent_inclusion',
        label: 'Hyresstruktur',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'Total hyra',
        options: [
            { value: 'Total hyra', label: 'Total hyra', code: 'total_rent', summary: 'Rent is all-inclusive.' },
            { value: 'Hyra exkl. tillägg', label: 'Hyra exkl. tillägg', code: 'rent_excl_surcharges', summary: 'Rent is stated excluding separate surcharges.' },
        ],
    },
    // Ändring av hyra
    {
        key: 'rent_adjustment',
        label: 'Justeringsmodell',
        type: 'select',
        section: 'andring-hyra',
        requiredAtFinal: true,
        default: 'Fast årlig höjning',
        options: [
            { value: 'Indexklausul enligt bilaga', label: 'Indexklausul enligt bilaga', code: 'index_clause', summary: 'Rent is index-adjusted per a separate index clause annex.' },
            { value: 'Fast årlig höjning', label: 'Fast årlig höjning per 1 januari', code: 'fixed_annual_increase', summary: 'Rent rises by a fixed percentage each year on 1 January.' },
        ],
    },
    {
        key: 'rent_index_annex',
        label: 'Bilaga – indexregleringsklausul',
        type: 'attachment_ref',
        section: 'andring-hyra',
        requiredAtFinal: true,
        attachmentDefaultName: 'Indexavtal',
        recommendedAttachmentTemplate: { key: 'indexavtal', version: 'v1' },
        visibleWhen: { rent_adjustment: 'Indexklausul enligt bilaga' },
        help: 'Välj eller skapa en indexregleringsbilaga (mall: Indexavtal).',
    },
    {
        key: 'fixed_increase_start_year',
        label: 'Första höjning fr.o.m. (år)',
        type: 'text',
        section: 'andring-hyra',
        visibleWhen: { rent_adjustment: 'Fast årlig höjning' },
        help: 'T.ex. "2027"',
        // Absolute year tied to this contract — opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'fixed_increase_percent',
        label: 'Höjning per år (%)',
        type: 'number',
        min: 0,
        max: 100,
        section: 'andring-hyra',
        visibleWhen: { rent_adjustment: 'Fast årlig höjning' },
    },
    // Kostnad för elström
    {
        key: 'electricity_cost',
        label: 'Kostnad för elström',
        type: 'select',
        section: 'elkostnad',
        requiredAtFinal: true,
        default: 'Ingår i hyran',
        options: [
            { value: 'Ingår i hyran', label: 'Ingår i hyran', code: 'included_in_rent', summary: 'Electricity cost is included in the rent.' },
            { value: 'Enligt mätare', label: 'Enligt mätare', code: 'by_meter', summary: 'Electricity is billed according to a meter.' },
            { value: 'Andel av gemensam förbrukning', label: 'Andel av gemensam förbrukning', code: 'shared_consumption', summary: 'Electricity is billed as a share of shared consumption.' },
            { value: 'Annat sätt', label: 'Annat sätt', code: 'other_method', summary: 'Electricity cost is calculated some other described way.' },
            { value: 'Ej relevant', label: 'Ej relevant', code: 'not_applicable', summary: 'Electricity cost is not applicable.' },
        ],
    },
    {
        key: 'electricity_cost_description',
        label: 'Beräkningsgrund (annat sätt)',
        type: 'textarea',
        section: 'elkostnad',
        visibleWhen: { electricity_cost: 'Annat sätt' },
    },
    // Mervärdesskatt
    {
        key: 'vat_status',
        label: 'Mervärdesskatt',
        type: 'select',
        section: 'moms',
        requiredAtFinal: true,
        default: 'Upplåtelsen är ej mervärdesskattepliktig',
        options: [
            {
                value: 'Upplåtelsen är mervärdesskattepliktig',
                label: 'Upplåtelsen är mervärdesskattepliktig',
                code: 'vat_liable',
                summary: 'The lease is subject to VAT.',
            },
            {
                value: 'Upplåtelsen är ej mervärdesskattepliktig',
                label: 'Upplåtelsen är ej mervärdesskattepliktig',
                code: 'vat_exempt',
                summary: 'The lease is not subject to VAT.',
            },
        ],
    },
    // Hyrans betalning
    {
        key: 'payment_period',
        label: 'Betalningsperiod',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'Kalenderkvartals början',
        options: [
            { value: 'Kalenderkvartals början', label: 'Kalenderkvartals början', code: 'quarterly', summary: 'Rent is paid at the start of each calendar quarter.' },
            { value: 'Kalendermånads början', label: 'Kalendermånads början', code: 'monthly', summary: 'Rent is paid at the start of each calendar month.' },
        ],
    },
    {
        key: 'payment_type',
        label: 'Betalningssätt',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'Enligt avisering i faktura',
        options: [
            { value: 'Enligt avisering i faktura', label: 'Enligt avisering i faktura (rekommenderas)', code: 'invoice', summary: 'Paid per the invoice/notice sent to the tenant.' },
            { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
            { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
        ],
    },
    {
        key: 'payment_account',
        label: 'Hyresvärdens konto (BG/PG + nummer)',
        type: 'text',
        section: 'betalning',
        help: 'T.ex. BG 123-4567',
        visibleWhen: { payment_type: ['Plusgiro', 'Bankgiro'] },
    },
    // Hantering av personuppgifter
    {
        key: 'privacy_info_annex',
        label: 'Information till hyresgäst om behandling av personuppgifter, se bilaga',
        type: 'boolean',
        section: 'personuppgifter',
        default: true,
    },
    // Särskilda bestämmelser
    {
        key: 'special_conditions',
        label: 'Särskilda bestämmelser',
        type: 'textarea',
        section: 'sarskilda-bestammelser',
        help: 'Ange eventuella särskilda villkor här.',
    },
];
export const sampleValues = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556677-8899',
    landlord_address: 'Sveavägen 100\n113 50 Stockholm',
    landlord_phone: '08-555 12 34',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    tenant_billing_address: 'Götgatan 35\n116 21 Stockholm',
    tenant_phone: '070-123 45 67',
    property_municipality: 'Stockholm',
    property_designation: 'Klockaren 5',
    garage_street: 'Götgatan 35',
    garage_form: 'Garageplats',
    garage_place_number: '17',
    garage_temperature: 'Varmgarage',
    area_sqm: 14,
    car_count: 1,
    ev_count: 1,
    shared_use: 'Alla dagar och tider',
    electricity: 'Med laddare',
    charger_type: 'Type 2',
    start_date: '2026-09-01',
    end_date: '2027-08-31',
    notice_months: 3,
    extension_months: 12,
    annual_rent: 21600,
    rent_inclusion: 'Hyra exkl. tillägg',
    rent_adjustment: 'Fast årlig höjning',
    fixed_increase_start_year: '2027',
    fixed_increase_percent: 3,
    electricity_cost: 'Enligt mätare',
    vat_status: 'Upplåtelsen är ej mervärdesskattepliktig',
    payment_period: 'Kalendermånads början',
    payment_type: 'Enligt avisering i faktura',
    privacy_info_annex: true,
    special_conditions: 'Garageplatsen är belägen på plan -1 och åtkomlig dygnet runt med tagg.',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, attachments, signatories, }) {
    const hasElectric = values.electricity === 'Med elanslutning för motorvärmare/kupévärmare' ||
        values.electricity === 'Med elanslutning för annan användning' ||
        values.electricity === 'Med laddare';
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsx(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessee", values: values }) })] }), _jsxs(Section, { sectionRef: "garage", title: "Garage / Garageplats", children: [_jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) })] }), _jsx("div", { className: "grid grid-cols-2 gap-6", children: _jsx(FieldRow, { label: "Adress f\u00F6r fastigheten", children: v(values.garage_street) }) }), _jsx("p", { className: "mt-3 text-[10.5px] font-medium", children: "Avser:" }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsxs("span", { className: "mr-4", children: [_jsx(Checkbox, { checked: values.garage_form === 'Helt garage' }), "Helt garage"] }), _jsxs("span", { className: "mr-4", children: [_jsx(Checkbox, { checked: values.garage_form === 'Garageplats' }), "Garageplats nr", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.garage_place_number) })] }), _jsxs("span", { className: "mr-4", children: [_jsx(Checkbox, { checked: values.garage_temperature === 'Kallgarage' }), "Kallgarage"] }), _jsxs("span", { children: [_jsx(Checkbox, { checked: values.garage_temperature === 'Varmgarage' }), "Varmgarage"] })] }), _jsx(FieldRow, { label: "Area (ca m\u00B2)", children: num(values.area_sqm) })] })] }), _jsxs(Section, { sectionRef: "anvandning", title: "Anv\u00E4ndning", children: [_jsx("p", { className: "mb-2 text-[10.5px] leading-relaxed", children: "Garaget / garageplatsen uppl\u00E5ts, om inget annat anges, i befintligt skick f\u00F6r att anv\u00E4ndas till uppst\u00E4llningsplats f\u00F6r f\u00F6ljande av hyresg\u00E4sten nyttjade fordon:" }), _jsxs(FieldRow, { label: "Antal personbilar", children: [num(values.car_count), " st."] }), _jsxs(FieldRow, { label: "Varav laddbara el-/hybridbilar", children: [num(values.ev_count), " st."] })] }), _jsxs(Section, { sectionRef: "samnyttjande", title: "Samnyttjande", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.shared_use === 'Alla dagar och tider' }), "Alla dagar och tider"] }), _jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.shared_use === 'Nedanstående dagar och tider' }), "Nedanst\u00E5ende dagar och tider"] }), _jsxs("span", { children: [_jsx(Checkbox, { checked: values.shared_use === 'Enligt bilaga' }), "Enligt bilaga"] })] }), cond(mode, values.shared_use === 'Nedanstående dagar och tider') && (_jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: ["Veckodagarna", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_days) }), ' ', "mellan kl", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_time_from) }), ' ', "och kl", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_time_to) }), "."] })), cond(mode, values.shared_use === 'Enligt bilaga') && (_jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: ["Nyttjandetiderna framg\u00E5r av", ' ', hasAttachmentRef(values.shared_use_annex)
                                ? attachmentLabel(attachments, values.shared_use_annex)
                                : 'bilaga ____', "."] }))] }), _jsxs(Section, { sectionRef: "elstrom", title: "Elstr\u00F6m", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.electricity === 'Utan elanslutning' }), "Utan elanslutning"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.electricity === 'Med elanslutning för motorvärmare/kupévärmare' }), "Med elanslutning f\u00F6r motorv\u00E4rmare/kup\u00E9v\u00E4rmare"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.electricity === 'Med elanslutning för annan användning' }), "Med elanslutning f\u00F6r annan anv\u00E4ndning, max", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.electricity_max_watt) }), ' ', "watt"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.electricity === 'Med laddare' }), "Med laddare, anslutningstyp", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.charger_type) })] })] }), _jsx(Section, { sectionRef: "hyrestid", title: "Hyrestid", children: _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(FieldRow, { label: "Fr.o.m.", children: v(values.start_date) }), _jsx(FieldRow, { label: "T.o.m.", children: v(values.end_date) })] }) }), _jsx(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gning och f\u00F6rl\u00E4ngning", children: _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: ["Upps\u00E4gning av detta kontrakt ska ske skriftligen minst", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.notice_months) }), ' ', "m\u00E5nad(er) f\u00F6re den avtalade hyrestidens utg\u00E5ng. I annat fall \u00E4r kontraktet f\u00F6r varje g\u00E5ng f\u00F6rl\u00E4ngt med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.extension_months) }), ' ', "m\u00E5nad(er)."] }) }), _jsxs(Section, { sectionRef: "hyra", title: "Hyra", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: ["Kronor", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.annual_rent) }), ' ', "per \u00E5r."] }), _jsxs("p", { className: "mt-1 text-[10.5px] leading-relaxed", children: [_jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.rent_inclusion === 'Total hyra' }), "Total hyra"] }), _jsxs("span", { children: [_jsx(Checkbox, { checked: values.rent_inclusion === 'Hyra exkl. tillägg' }), "Hyra exkl. till\u00E4gg"] })] })] }), _jsxs(Section, { sectionRef: "andring-hyra", title: "\u00C4ndring av hyra", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.rent_adjustment === 'Indexklausul enligt bilaga' }), "Indexklausul enligt bilaga"] }), cond(mode, values.rent_adjustment === 'Indexklausul enligt bilaga') && (_jsxs("p", { className: "mt-1 text-[10.5px] leading-relaxed", children: ["Indexreglering av hyran sker enligt s\u00E4rskild bilaga.", ' ', hasAttachmentRef(values.rent_index_annex)
                                ? attachmentLabel(attachments, values.rent_index_annex)
                                : 'Bilaga ____', "."] })), _jsxs("p", { className: "mt-1 text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.rent_adjustment === 'Fast årlig höjning' }), "Per 1 januari varje \u00E5r fr.o.m.", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.fixed_increase_start_year) }), ' ', "med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.fixed_increase_percent) }), ' ', "procent."] })] }), _jsxs(Section, { sectionRef: "elkostnad", title: "Kostnad f\u00F6r elstr\u00F6m", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.electricity_cost === 'Ingår i hyran' }), "Ing\u00E5r i hyran"] }), _jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.electricity_cost === 'Enligt mätare' }), "Enligt m\u00E4tare"] }), _jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.electricity_cost === 'Andel av gemensam förbrukning' }), "Andel av gemensam f\u00F6rbrukning"] }), _jsxs("span", { children: [_jsx(Checkbox, { checked: values.electricity_cost === 'Annat sätt' }), "Annat s\u00E4tt"] })] }), cond(mode, values.electricity_cost === 'Annat sätt') && (_jsx("div", { className: "mt-2 whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: v(values.electricity_cost_description) }))] }), _jsxs(Section, { sectionRef: "moms", title: "Merv\u00E4rdesskatt (moms)", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.vat_status === 'Upplåtelsen är mervärdesskattepliktig' }), "Uppl\u00E5telsen \u00E4r merv\u00E4rdesskattepliktig"] }), _jsxs("p", { className: "mt-1 text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.vat_status === 'Upplåtelsen är ej mervärdesskattepliktig' }), "Uppl\u00E5telsen \u00E4r ej merv\u00E4rdesskattepliktig"] }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-600", children: "Om uppl\u00E5telsen \u00E4r merv\u00E4rdesskattepliktig ska hyresg\u00E4sten ut\u00F6ver hyran betala vid varje tillf\u00E4lle g\u00E4llande moms." })] }), _jsxs(Section, { sectionRef: "betalning", title: "Hyrans betalning", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: ["Hyran tillsammans med f\u00F6rekommande till\u00E4gg ska betalas i f\u00F6rskott senast sista vardagen f\u00F6re", ' ', _jsxs("span", { className: "mr-3", children: [_jsx(Checkbox, { checked: values.payment_period === 'Kalenderkvartals början' }), "kalenderkvartals b\u00F6rjan"] }), _jsxs("span", { children: [_jsx(Checkbox, { checked: values.payment_period === 'Kalendermånads början' }), "kalenderm\u00E5nads b\u00F6rjan"] }), "."] }), _jsxs("div", { className: "mt-2 flex flex-col gap-1 text-[10.5px] leading-relaxed", children: [_jsxs("p", { children: [_jsx(Checkbox, { checked: values.payment_type === 'Enligt avisering i faktura' }), "Hyran betalas enligt avisering."] }), _jsxs("p", { children: [_jsx(Checkbox, { checked: values.payment_type === 'Plusgiro' || values.payment_type === 'Bankgiro' }), "Hyran betalas genom ins\u00E4ttning p\u00E5 Hyresv\u00E4rdens konto:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.payment_account) })] })] })] }), _jsx(Section, { sectionRef: "personuppgifter", title: "Hantering av personuppgifter", children: _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: !!values.privacy_info_annex }), "Information till hyresg\u00E4st om behandling av personuppgifter, se bilaga."] }) }), _jsx(Section, { sectionRef: "sarskilda-bestammelser", title: "S\u00E4rskilda best\u00E4mmelser", children: _jsx("div", { className: "whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: v(values.special_conditions) }) }), _jsx(ClauseBlock, { title: "Avtalsvillkor", body: 'Hyresgästen får inte utan hyresvärdens skriftliga medgivande överlåta hyresrätten eller sätta annan i sitt ställe.\n\n' +
                    'a) Hyresgästen får inte överlåta hyresrätten eller hyra ut garaget/garageplatsen i andra hand utan hyresvärdens medgivande.\n\n' +
                    'b) Hyresgästen förbinder sig\n' +
                    '• att i garaget inte införa andra typer av fordon än som anges i detta kontrakt,\n' +
                    '• att i garaget inte utföra andra än smärre reparations- och justeringsarbeten såsom byte av tändstift, däck o.d,\n' +
                    '• att i garaget inte utföra biltvätt om inte hyresvärden tillhandahåller särskild för ändamålet iordningsställd spolplatta,\n' +
                    '• att teckna och vidmakthålla erforderlig brand- och stöldförsäkring för nämnda fordon, att följa gällande brandförsäkringsvillkor och garageföreskrifter,\n' +
                    '• att ansvara för all skada inom garaget som vållas av hyresgästen själv, hyresgästens hushållsmedlemmar eller av hyresgästen anlitade personer\n\n' +
                    'c) Hyresvärden är inte ersättningsskyldig för genom brand, åverkan eller annan anledning eventuellt uppkommande skada på eller stöld av hyresgästens fordon med tillbehör och reservdelar samt däri förvarade effekter, såvida inte skadan vållats av hyresvärden eller dennes personal.\n\n' +
                    'd) För upplåtelse av garage/garageplats med eluttag eller laddare gäller särskilt\n' +
                    '• att för ändamålet godkänd adapter eller anslutningskabel utöver eventuell fast kabel tillhandahålls av hyresgästen,\n' +
                    '• att endast elapparater, adapter och anslutningskabel som uppfyller gällande säkerhetskrav får användas,\n' +
                    '• att eluttaget alltid ska hållas låst,\n' +
                    '• att anslutningskabel inte får vara ansluten till eluttaget då kabeln inte är kopplad till elapparat,\n' +
                    '• att hyresgästen omedelbart ska anmäla uppkommet fel på eluttaget eller laddaren till hyresvärden eller dennes ombud,\n' +
                    '• att hyresgästen svarar för att elapparat, anslutet fordon, anslutningskabel och adapter vid användningen är i fullgott skick,\n' +
                    '• att endast det eluttag eller laddpunkt som tillhör garageplatsen får användas samt\n' +
                    '• att säkerhetsföreskrifter och bruksanvisning som framgår av anslag vid laddare eller som tillställts hyresgästen på annat sätt måste iakttas vid användning av laddaren.' }), cond(mode, hasElectric) && (_jsx(ClauseBlock, { title: "S\u00E4rskilt f\u00F6r garage med eluttag eller laddare", body: 'För upplåtelse av garage / garageplats med eluttag eller laddare gäller särskilt:\n\n' +
                    '• att för ändamålet godkänd adapter och anslutningskabel utöver fast kabel tillhandahålls av hyresgästen,\n' +
                    '• att endast elapparat, adapter och anslutningskabel som uppfyller gällande säkerhetskrav får användas,\n' +
                    '• att eluttaget inte får användas för laddning av el-/hybridfordon om det inte särskilt godkänts av hyresvärden,\n' +
                    '• att eluttaget alltid ska hållas låst,\n' +
                    '• att anslutningskabel inte får vara ansluten till eluttaget då kabeln inte är kopplad till elapparat,\n' +
                    '• att hyresgästen omedelbart till hyresvärden ska anmäla uppkomna fel på eluttaget eller laddaren,\n' +
                    '• att hyresgästen ansvarar för att elapparat, anslutet fordon, anslutningskabel och adapter vid användningen är i fullgott skick,\n' +
                    '• att endast det eluttag eller laddpunkt som tillhör garaget / garageplatsen får användas,\n' +
                    '• att säkerhetsföreskrifter och bruksanvisning som framgår av anslag vid laddaren eller som tillställts hyresgästen på annat sätt iakttas vid användning av laddaren.' })), _jsx(ClauseBlock, { title: "\u00D6verl\u00E5telse, f\u00F6rverkande och ordningsf\u00F6reskrifter", body: 'Hyresrätten till garaget / garageplatsen enligt detta avtal får inte utan hyresvärdens medgivande överlåtas eller inskrivas.\n\n' +
                    'Hyresgästens rätt enligt detta avtal är förverkad och hyresvärden berättigad att säga upp avtalet till omedelbart upphörande om hyresgästen inte i rätt tid betalar angiven hyra tillsammans med förekommande tillägg och moms eller i övrigt bryter mot bestämmelserna i 8 kap. 23 § jordabalken.\n\n' +
                    'För nyttjanderätten av garaget / garageplatsen gäller särskilda ordningsföreskrifter.' }), _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                        ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map